import React from "react";
import classNames from "classnames";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";

import { UseCases } from "@planzone/core";
import { ProjectElementType } from "@planzone/dto";
import { useRetrieveTimeSheet } from "@planzone/view-models";

import { useTranslation } from "react-i18next";
import { useModal } from "@root/client/modal";
import { FolderIcon } from "@common/folderIcon";
import { useToggle, useUtils } from "@common/treeToggle";
import { AutoFocusContext } from "../../autoFocusCtx";
import { Safeguard } from "@scenes/safeguard";
import { ScrollingText } from "@common/scrollingText";
import { ProjectPopup, ToggleIcon } from "@scenes/projectPopup";
import { ListToggleArrow } from "@common/listToggleArrow";
import { Flag } from "@common/infiniteScroll";
import { Loader } from "../../loader";
import { RecordList } from "../recordList";
import { Props } from "./types";
import { List } from "./list";
import { Task } from "./task";
import { renderWithTreeCtx } from "./utils";
import styles from "./styles.scss";

function Project({
  project,
  resource,
  filter,
  areDetailsDisplayed,
  isReloading,
  showCompletionColumn,
  timesheetDeadline,
}: Props) {
  const { t } = useTranslation();
  const { pushView } = useModal();
  const { isOpen, toggle } = useToggle(
    `${resource.uuid}:${project.uuid}`,
    1,
    false,
  );
  const { isOpen: isTreeItemOpen } = useUtils();
  const {
    isRequestPending,
    currentPage,
    timeSheet,
    isDirty,
    isRequestFailure: isTimeSheetFailure,
    canLoadMore,
    retrieveTimeSheet,
  } = useRetrieveTimeSheet(
    resource,
    project,
    { ...filter, integrationScope: "RESOURCE" },
    30,
  );
  const { lastAllocatedProjectUuid, lastAllocatedResourceUuid } =
    React.useContext(AutoFocusContext);
  const projectRef = React.useRef<HTMLDivElement>(null);
  const [menuState, setMenuState] = React.useState({
    isDisplayed: false,
    x: 0,
    y: 0,
  });

  React.useEffect(() => {
    if (
      lastAllocatedProjectUuid === project.uuid &&
      lastAllocatedResourceUuid === resource.uuid
    ) {
      if (!isOpen) {
        toggle();
      }
      projectRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [lastAllocatedProjectUuid, lastAllocatedResourceUuid]);

  React.useEffect(() => {
    !isRequestPending &&
      !isTimeSheetFailure &&
      isDirty &&
      retrieveTimeSheet({
        project,
        resource,
        filter: { ...filter, integrationScope: "RESOURCE" },
        page: 0,
        size: timeSheet.elements.length + 10,
      });
  }, [isDirty, isRequestPending]);

  return (
    <div ref={projectRef} className={styles.project}>
      <div className={styles.main}>
        <div className={styles.header} style={{ paddingLeft: `4rem` }}>
          <div className={styles.iconBox} onClick={toggle}>
            <ListToggleArrow isOpen={isOpen} />
            <FolderIcon className={styles.folderIcon} isOpen={isOpen} />
          </div>
          <Safeguard useCases={[UseCases.editProject]} context={{ project }}>
            {({ canEditProject }) => (
              <>
                <ScrollingText speed={100}>
                  {canEditProject ? (
                    <p
                      className={`${
                        project.memberDeactivated
                          ? styles.deactivated
                          : styles.name
                      }`}
                      onClick={
                        project.resourceRemoved
                          ? undefined
                          : () =>
                              pushView({
                                key: UseCases.retrieveProject,
                                data: { project, isMultiProject: true },
                              })
                      }
                      style={
                        project.resourceRemoved ? { cursor: "default" } : {}
                      }
                    >
                      {project.name}
                    </p>
                  ) : (
                    <p
                      className={`${styles.link} ${
                        project.memberDeactivated
                          ? styles.deactivated
                          : styles.name
                      }`}
                      style={
                        project.resourceRemoved
                          ? { cursor: "default" }
                          : { cursor: "pointer" }
                      }
                      onClick={
                        project.resourceRemoved
                          ? undefined
                          : () =>
                              (window.location.href = `/projects/${project.uuid}`)
                      }
                    >
                      {project.name}
                    </p>
                  )}
                </ScrollingText>
                {project.resourceRemoved && (
                  <span className={styles.removedBadge}>
                    {t("timesheetPage.resourceRemoved")}
                  </span>
                )}
                {canEditProject && !project.resourceRemoved && (
                  <div
                    className={styles.projectPopup}
                    onMouseEnter={(e) => {
                      const target = e.currentTarget;
                      const rect = target.getBoundingClientRect();

                      setMenuState((s) => ({
                        ...s,
                        isDisplayed: !s.isDisplayed,
                        x: rect.left,
                        y: rect.top,
                      }));
                    }}
                    onMouseLeave={() =>
                      setMenuState((s) => ({
                        ...s,
                        isDisplayed: false,
                      }))
                    }
                  >
                    <ToggleIcon className={styles.popupIcon} />
                    {menuState.isDisplayed &&
                      createPortal(
                        <div
                          className={styles.projectPopupMain}
                          style={{
                            top: menuState.y,
                            left: menuState.x,
                          }}
                        >
                          <ProjectPopup projectId={project.uuid} />
                        </div>,
                        document.body,
                      )}
                  </div>
                )}
              </>
            )}
          </Safeguard>
        </div>
        <div className={styles.recordList}>
          <RecordList
            type="project"
            {...project}
            project={project}
            resource={resource}
            records={[]}
            allocatedHours={project.totalOfAllocatedHours}
            spentTime={project.totalOfSpentTime}
            spentTimeInWeek={project.totalOfSpentTimeInWeek}
            remainingTime={project.totalOfRemainingTime}
            areDetailsDisplayed={areDetailsDisplayed}
            isReloading={isReloading}
            showCompletionColumn={showCompletionColumn}
          />
        </div>
      </div>
      {isOpen && (
        <>
          {renderWithTreeCtx(timeSheet, isTreeItemOpen, (element) =>
            element.type === ProjectElementType.LIST ? (
              <List
                key={element.uuid}
                list={element}
                project={project}
                resource={resource}
                depth={element.depth + 1}
                areDetailsDisplayed={areDetailsDisplayed}
                isReloading={isReloading}
                showCompletionColumn={showCompletionColumn}
                resourceRemoved={!!project.resourceRemoved}
              />
            ) : (
              <Task
                key={element.uuid}
                task={element}
                project={project}
                resource={resource}
                depth={element.depth + 1}
                areDetailsDisplayed={areDetailsDisplayed}
                isReloading={isReloading}
                showCompletionColumn={showCompletionColumn}
                timesheetDeadline={timesheetDeadline}
                isResourceInProject={timeSheet.isResourceInProject}
                resourceRemoved={!!project.resourceRemoved}
              />
            ),
          )}
          {isRequestPending && !isDirty && <Loader isTsLoader />}
          <Flag
            name={`${resource.uuid}:${project.uuid}`}
            isDisabled={isRequestPending || !canLoadMore}
            retrieveItems={() =>
              retrieveTimeSheet({
                project,
                resource,
                filter: { ...filter, integrationScope: "RESOURCE" },
                page: currentPage,
                size: 30,
              })
            }
          />
        </>
      )}
    </div>
  );
}

export { Project };
